/* ---------------------------------------------------------------------------
   La lógica PURA de la pestaña "Antecedentes generales": qué secciones tiene,
   cuáles están completas y qué queda pendiente.

   Vive FUERA del componente por la misma razón que `registroDeTerreno.ts`: un
   punto verde del índice o un pendiente que miente no rompe nada a la vista, y
   dentro del árbol un test no distingue una cuenta buena de una cercana.

   Todo se DERIVA del estado del formulario (y de dos hechos que le llegan de
   afuera: la imagen satelital guardada y el punto GPS de terreno). No hay
   ninguna columna nueva ni nada que el servidor mande calculado.
--------------------------------------------------------------------------- */

export type ClaveDeSeccion =
  | "ident"
  | "resp"
  | "ubic"
  | "roles"
  | "imagenes"
  | "contacto"
  | "desc"
  | "contenidos"
  | "notas";

export const SECCIONES_DE_ANTECEDENTES: { clave: ClaveDeSeccion; n: string; etiqueta: string }[] = [
  { clave: "ident", n: "01", etiqueta: "Identificación" },
  { clave: "resp", n: "02", etiqueta: "Responsables" },
  { clave: "ubic", n: "03", etiqueta: "Ubicación del predio" },
  { clave: "roles", n: "04", etiqueta: "Roles del informe" },
  { clave: "imagenes", n: "05", etiqueta: "Imágenes del informe" },
  { clave: "contacto", n: "06", etiqueta: "Contacto y superficie" },
  { clave: "desc", n: "07", etiqueta: "Descripciones" },
  { clave: "contenidos", n: "08", etiqueta: "Contenidos del informe" },
  { clave: "notas", n: "09", etiqueta: "Notas u observaciones" },
];

/** El `id` del DOM de cada tarjeta: es a donde apuntan el índice y los pendientes. */
export function idDeLaSeccion(clave: ClaveDeSeccion): string {
  return `antecedentes-${clave}`;
}

/** Lo que el formulario (y dos datos de afuera) le dicen a esta lógica. */
export interface EntradaDeAntecedentes {
  numero_informe: string;
  cliente_nombre: string;
  propiedad_nombre: string;
  solicitante_nombre: string;
  telefono_contacto: string;
  email_contacto: string;
  responsables: Record<string, string>;
  coordenada: { latitud: number; longitud: number } | null;
  /** Hay números escritos que todavía no forman una coordenada. */
  coordenadaIncompleta: boolean;
  roles: { rol_sii: string }[];
  /** La imagen satelital ya está guardada en el servidor. */
  hayImagen: boolean;
  descripcion_ubicacion: string;
  descripcion: string;
  descripcion_ubicacion_validada: boolean;
  descripcion_validada: boolean;
  /** Cuántos contenidos hay elegidos. */
  contenidos: number;
  notas: string;
  /** El encargado confirmó un punto de acceso y el formulario no lo usa. */
  puntoDeTerrenoSinAplicar?: boolean;
}

const escrito = (texto: string) => texto.trim() !== "";

export function seccionesCompletas(e: EntradaDeAntecedentes): Record<ClaveDeSeccion, boolean> {
  return {
    ident: escrito(e.numero_informe) && escrito(e.cliente_nombre) && escrito(e.propiedad_nombre),
    resp: Object.values(e.responsables).length >= 4 && Object.values(e.responsables).every(escrito),
    ubic: e.coordenada !== null && !e.coordenadaIncompleta,
    roles: e.roles.some((rol) => escrito(rol.rol_sii)),
    imagenes: e.hayImagen,
    contacto:
      escrito(e.solicitante_nombre) && escrito(e.telefono_contacto) && escrito(e.email_contacto),
    // Texto Y validación en las dos: la casilla dice "yo lo revisé", y no hay
    // nada que revisar en un campo vacío.
    desc:
      escrito(e.descripcion_ubicacion) &&
      escrito(e.descripcion) &&
      e.descripcion_ubicacion_validada &&
      e.descripcion_validada,
    contenidos: e.contenidos > 0,
    notas: escrito(e.notas),
  };
}

export function avanceDeLaPestana(completas: Record<ClaveDeSeccion, boolean>) {
  const total = SECCIONES_DE_ANTECEDENTES.length;
  const hechas = SECCIONES_DE_ANTECEDENTES.filter(({ clave }) => completas[clave]).length;

  return { hechas, total, porcentaje: (hechas / total) * 100 };
}

export interface Pendiente {
  /** La tarjeta a la que lleva el aviso. */
  seccion: ClaveDeSeccion;
  texto: string;
}

/**
 * Lo que falta en ESTA pestaña, en el orden en que conviene resolverlo.
 *
 * NO es la lista de "secciones incompletas": las notas, la imagen y el contacto
 * pueden no estar completas sin que eso sea un pendiente. Es solo lo que la
 * persona tiene que decidir antes de dar la pestaña por terminada.
 */
export function pendientesDeLaPestana(e: EntradaDeAntecedentes): Pendiente[] {
  const lista: Pendiente[] = [];

  if (e.puntoDeTerrenoSinAplicar) {
    lista.push({
      seccion: "ubic",
      texto: "Hay un punto de acceso confirmado en terreno sin aplicar.",
    });
  }

  if (e.coordenadaIncompleta) {
    lista.push({ seccion: "ubic", texto: "La coordenada está a medias. Va completa o no va." });
  } else if (e.coordenada === null) {
    lista.push({
      seccion: "ubic",
      texto: "Falta marcar el predio en el mapa: sin coordenada no hay distancias a comparables.",
    });
  }

  if (e.contenidos === 0) {
    lista.push({ seccion: "contenidos", texto: "Elige al menos un contenido para el informe." });
  }

  // Un campo VACÍO no cuenta: pedir validar un texto que nadie escribió sería
  // un aviso permanente en todo informe recién aceptado.
  const sinRevisar =
    (escrito(e.descripcion_ubicacion) && !e.descripcion_ubicacion_validada) ||
    (escrito(e.descripcion) && !e.descripcion_validada);

  if (sinRevisar) {
    lista.push({ seccion: "desc", texto: "Quedan descripciones sin marcar como revisadas." });
  }

  return lista;
}

/**
 * Cuántos campos están sucios, para el "N cambios sin guardar" de la barra.
 *
 * Recibe el `dirtyFields` de react-hook-form, que es un árbol con `true` en
 * las hojas editadas. Cuenta HOJAS y no ramas: editar la latitud y la
 * longitud son dos cambios, aunque vivan en el mismo objeto.
 */
export function contarCambios(sucios: unknown): number {
  if (sucios === true) return 1;
  if (Array.isArray(sucios)) return sucios.reduce((total, hijo) => total + contarCambios(hijo), 0);
  if (sucios && typeof sucios === "object") {
    return Object.values(sucios).reduce((total: number, hijo) => total + contarCambios(hijo), 0);
  }

  return 0;
}
